/**
 * FeatureFlagsPanel — admin matrix to toggle which subscription tiers can
 * access each feature. Rows = features (grouped by category); columns = tiers.
 * Each cell is a checkbox; flipping it PUTs the new `allowed_tiers` to the
 * server.
 *
 * Surfaced under Admin → Features tab.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { invalidateFeaturesCache } from '../hooks/useFeatures';

export const FeatureFlagsPanel = () => {
  const { api } = useAuth();
  const [flags, setFlags] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Track in-flight cell updates so we can disable + spinner the right one.
  const [updating, setUpdating] = useState({}); // { [`${key}:${tier_id}`]: true }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [f, t] = await Promise.all([
          api.get('/admin/feature-flags'),
          api.get('/admin/tiers'),
        ]);
        if (!mounted) return;
        setFlags(f.data?.flags || []);
        // Filter out archived legacy tiers from the matrix — surface only the
        // ones admins actively sell. Order: cheapest → priciest by `price`.
        const visible = (t.data?.tiers || [])
          .filter((x) => !x.archived)
          .sort((a, b) => (a.price || 0) - (b.price || 0));
        setTiers(visible);
      } catch (e) {
        toast.error('Failed to load feature flags');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [api]);

  const grouped = useMemo(() => {
    const out = {};
    for (const f of flags) {
      const cat = f.category || 'Other';
      (out[cat] = out[cat] || []).push(f);
    }
    return out;
  }, [flags]);

  const isAllowed = (flag, tierId) => (flag.allowed_tiers || []).includes(tierId);

  const toggle = async (flag, tierId, nextAllowed) => {
    const cellKey = `${flag.key}:${tierId}`;
    setUpdating((u) => ({ ...u, [cellKey]: true }));
    const current = new Set(flag.allowed_tiers || []);
    if (nextAllowed) current.add(tierId);
    else current.delete(tierId);
    const allowed_tiers = Array.from(current);
    // Optimistic update — patch local state immediately, roll back on error.
    setFlags((rows) => rows.map((r) => r.key === flag.key ? { ...r, allowed_tiers } : r));
    try {
      await api.put(`/admin/feature-flags/${flag.key}`, { allowed_tiers });
      invalidateFeaturesCache(); // ensure /me/features re-fetches on next mount
    } catch (e) {
      setFlags((rows) => rows.map((r) => r.key === flag.key ? { ...r, allowed_tiers: flag.allowed_tiers || [] } : r));
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setUpdating((u) => {
        const { [cellKey]: _, ...rest } = u;
        return rest;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <Card data-testid="feature-flags-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-indigo-600" />
          Feature Flags
        </CardTitle>
        <CardDescription>
          Control which subscription plans get access to each feature. Changes apply instantly to every user. You're always allowed (admins bypass every gate).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(grouped).map(([category, rows]) => (
          <div key={category} className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide">
              {category}
            </h3>
            <div className="overflow-x-auto border border-zinc-200 rounded-md">
              <table className="w-full text-sm" data-testid={`flags-table-${category.toLowerCase()}`}>
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 font-medium text-zinc-600 w-1/3">Feature</th>
                    {tiers.map((t) => (
                      <th key={t.id} className="text-center p-3 font-medium text-zinc-600">
                        <div className="flex flex-col items-center gap-0.5">
                          <span>{t.name}</span>
                          <span className="text-[10px] text-zinc-400 font-normal">${t.price}/mo</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((flag) => (
                    <tr key={flag.key} className="border-t border-zinc-100 hover:bg-zinc-50/50">
                      <td className="p-3">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-zinc-900 flex items-center gap-1.5">
                              {flag.name}
                              {flag.is_locked && (
                                <Lock className="w-3 h-3 text-zinc-400" title="Built-in feature — can't be deleted" />
                              )}
                            </div>
                            {flag.description && (
                              <p className="text-xs text-zinc-500 mt-0.5 leading-snug">
                                {flag.description}
                              </p>
                            )}
                            <code className="text-[10px] text-zinc-400 font-mono">{flag.key}</code>
                          </div>
                        </div>
                      </td>
                      {tiers.map((t) => {
                        const allowed = isAllowed(flag, t.id);
                        const cellKey = `${flag.key}:${t.id}`;
                        const busy = !!updating[cellKey];
                        return (
                          <td key={t.id} className="p-3 text-center">
                            <label
                              className={`inline-flex items-center justify-center cursor-pointer ${busy ? 'opacity-50' : ''}`}
                              data-testid={`flag-${flag.key}-${t.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={allowed}
                                disabled={busy}
                                onChange={(e) => toggle(flag, t.id, e.target.checked)}
                                className="w-4 h-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                              />
                              {busy && <Loader2 className="w-3 h-3 ml-1 animate-spin text-zinc-400" />}
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
          <b>Tip:</b> Use this to launch a feature to Starter as a promotion, or to lock a Pro-only feature behind paid tiers.
          Free-tier users see an "upgrade" card in the app when they try to use a locked feature.
        </div>
      </CardContent>
    </Card>
  );
};

export default FeatureFlagsPanel;
